import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState, useEffect, useMemo } from 'react'
import { Users, Search, ChevronLeft, ChevronRight, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useAuth } from '@/contexts/AuthContext'
import { api } from '@/services/api'
import { DeleteModal } from '@/components/ui/delete-modal'
import { PageSkeleton } from '@/components/ui/page-skeleton'

export const Route = createFileRoute('/projects/users-tracking')({
  component: UsersTracking,
})

interface UserTracking {
  id: string
  email: string
  nom: string
  role: 'admin' | 'client' | 'super_admin'
  updated_at: string | null
  last_login: string | null
  client_nom?: string | null
}

interface UserTrackingResponse {
  data: UserTracking[]
  count: number
  message: string
}

function UsersTracking() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin'

  const [users, setUsers] = useState<UserTracking[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 10
  const [deletingUser, setDeletingUser] = useState<UserTracking | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  useEffect(() => {
    if (!isAdmin) {
      navigate({ to: '/projects' })
      return
    }
    fetchUsers()
  }, [isAdmin, navigate])

  const fetchUsers = async () => {
    setLoading(true)
    try {
      const response = await api.get<UserTrackingResponse>('/users')
      const usersData = response.data?.data || []
      
      // Enrich users with client information for clients
      const enrichedUsers = await Promise.all(
        usersData.map(async (u) => {
          if (u.role === 'client') {
            // For clients, we can use the nom as client_nom
            return { ...u, client_nom: u.nom }
          }
          return u
        })
      )
      
      setUsers(enrichedUsers)
    } catch (error) {
      console.error('Erreur lors de la récupération des utilisateurs:', error)
      setUsers([])
    } finally {
      setLoading(false)
    }
  }

  // Helper function to get role label (must be defined before useMemo)
  const getRoleLabel = (role: string): string => {
    switch (role) {
      case 'admin':
        return 'Admin'
      case 'super_admin':
        return 'Admin'
      case 'client':
        return 'Client'
      default:
        return role
    }
  }

  // Filter users based on search query
  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) return users
    
    const query = searchQuery.toLowerCase().trim()
    return users.filter((u) => {
      const emailMatch = u.email.toLowerCase().includes(query)
      const typeMatch = getRoleLabel(u.role).toLowerCase().includes(query)
      const clientMatch = u.client_nom?.toLowerCase().includes(query) || false
      return emailMatch || typeMatch || clientMatch
    })
  }, [users, searchQuery])

  // Pagination calculations
  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / itemsPerPage))
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const paginatedUsers = filteredUsers.slice(startIndex, endIndex)

  // Reset to page 1 if current page is out of bounds after filtering
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1)
    }
  }, [filteredUsers.length, totalPages, currentPage])

  // Statistics
  const stats = useMemo(() => {
    const total = users.length
    const admins = users.filter(u => u.role === 'admin' || u.role === 'super_admin').length
    const clients = users.filter(u => u.role === 'client').length
    return { total, admins, clients }
  }, [users])

  const getRoleBadgeColor = (role: string): string => {
    switch (role) {
      case 'admin':
      case 'super_admin':
        return 'bg-blue-100 text-blue-700 border-blue-200'
      case 'client':
        return 'bg-gray-100 text-gray-700 border-gray-200'
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200'
    }
  }

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return 'Jamais'
    try {
      const date = new Date(dateString)
      const now = new Date()
      const diffMs = now.getTime() - date.getTime()
      const diffMins = Math.floor(diffMs / 60000)
      const diffHours = Math.floor(diffMs / 3600000)
      const diffDays = Math.floor(diffMs / 86400000)
      const diffMonths = Math.floor(diffDays / 30)
      const diffYears = Math.floor(diffDays / 365)

      const day = date.getDate()
      const monthNames = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.']
      const month = monthNames[date.getMonth()]
      const year = date.getFullYear()
      const hours = date.getHours().toString().padStart(2, '0')
      const minutes = date.getMinutes().toString().padStart(2, '0')
      const formattedDate = `${day} ${month} ${year} à ${hours}:${minutes}`

      if (diffMins < 1) return `${formattedDate} (il y a moins d'une minute)`
      if (diffMins < 60) return `${formattedDate} (il y a ${diffMins} minute${diffMins > 1 ? 's' : ''})`
      if (diffHours < 24) return `${formattedDate} (il y a ${diffHours} heure${diffHours > 1 ? 's' : ''})`
      if (diffDays < 30) return `${formattedDate} (il y a ${diffDays} jour${diffDays > 1 ? 's' : ''})`
      if (diffMonths < 12) return `${formattedDate} (il y a environ ${diffMonths} mois)`
      return `${formattedDate} (il y a environ ${diffYears} an${diffYears > 1 ? 's' : ''})`
    } catch (error) {
      return 'Date invalide'
    }
  }

  const handleDeleteUser = async () => {
    if (!deletingUser) return

    setIsDeleting(true)
    try {
      await api.delete(`/users/${deletingUser.id}`)
      // Remove user from list
      setUsers(users.filter(u => u.id !== deletingUser.id))
      setDeletingUser(null)
    } catch (error: any) {
      console.error('Erreur lors de la suppression de l\'utilisateur:', error)
      const errorMessage = error?.response?.data?.detail || error?.message || 'Erreur lors de la suppression de l\'utilisateur'
      alert(errorMessage)
    } finally {
      setIsDeleting(false)
    }
  }

  if (!isAdmin) return null

  return (
    <div className="min-h-screen">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Title */}
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 flex items-center gap-2">
            Suivi des utilisateurs
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Vue d'ensemble et suivi de l'activité des utilisateurs
          </p>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="rounded-xl shadow-lg border border-gray-200 p-4 sm:p-6" style={{ backgroundColor: '#1CB5C808' }}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-cyan-100 flex items-center justify-center flex-shrink-0">
                <Users className="w-5 h-5 text-cyan-600" />
              </div>
              <div>
                <div className="text-sm font-medium text-gray-700">Total utilisateurs</div>
                <div className="text-2xl font-bold text-gray-900 mt-1">{stats.total}</div>
              </div>
            </div>
          </div>
          <div className="rounded-xl shadow-lg border border-gray-200 p-4 sm:p-6" style={{ backgroundColor: '#1CB5C808' }}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <div className="text-sm font-medium text-gray-700">Administrateurs</div>
                <div className="text-2xl font-bold text-gray-900 mt-1">{stats.admins}</div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-4 sm:p-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                <Users className="w-5 h-5 text-gray-600" />
              </div>
              <div>
                <div className="text-sm font-medium text-gray-700">Clients</div>
                <div className="text-2xl font-bold text-gray-900 mt-1">{stats.clients}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Latest Activity Section */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-4 sm:p-6 mb-6">
          <div className="mb-4">
            <h2 className="text-xl font-bold text-gray-800 mb-1">Dernières activités utilisateurs</h2>
            <p className="text-sm text-gray-500">Suivi de la dernière activité des utilisateurs sur la plateforme</p>
          </div>

          {/* Search Bar */}
          <div className="mb-4">
            <Input
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value)
                setCurrentPage(1) // Reset to first page on search
              }}
              placeholder="Rechercher par email, type ou client..."
              leftIcon={<Search className="w-5 h-5 text-gray-400" />}
              className="bg-gray-50 border-gray-200 focus:ring-cyan-400 focus:border-transparent h-12 text-base py-3"
            />
          </div>

          {/* Table */}
          {loading ? (
            <PageSkeleton variant="table" />
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 text-left text-gray-600 font-medium">
                      <th className="py-3 px-4">Email</th>
                      <th className="py-3 px-4">Type</th>
                      <th className="py-3 px-4">Client associé</th>
                      <th className="py-3 px-4">Dernière activité</th>
                      <th className="py-3 px-4 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedUsers.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="py-12 text-center text-gray-500">
                          {searchQuery ? 'Aucun utilisateur trouvé' : 'Aucun utilisateur'}
                        </td>
                      </tr>
                    ) : (
                      paginatedUsers.map((user) => (
                        <tr key={user.id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-4 text-gray-900">{user.email}</td>
                          <td className="py-3 px-4">
                            <span className={`inline-flex px-3 py-1 rounded-full border text-xs font-medium ${getRoleBadgeColor(user.role)}`}>
                              {getRoleLabel(user.role)}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-gray-700">
                            {user.role === 'client' ? user.client_nom || 'N/A' : 'N/A'}
                          </td>
                          <td className="py-3 px-4 text-gray-700">
                            {formatDate(user.last_login)}
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex justify-center">
                              <button
                                onClick={() => setDeletingUser(user)}
                                className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title="Supprimer l'utilisateur"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {filteredUsers.length > 0 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
                  <div className="text-sm text-gray-600">
                    Affichage {startIndex + 1} - {Math.min(endIndex, filteredUsers.length)} sur {filteredUsers.length} utilisateurs
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="bg-white border-gray-200 hover:bg-gray-50"
                    >
                      <ChevronLeft className="w-4 h-4" />
                      Précédent
                    </Button>
                    <span className="text-sm text-gray-600 px-3">
                      Page {currentPage}/{totalPages}
                    </span>
                    <Button
                      variant="outline"
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      className="bg-white border-gray-200 hover:bg-gray-50"
                    >
                      Suivant
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {/* Delete User Modal */}
      <DeleteModal
        open={deletingUser !== null}
        onOpenChange={(open) => !open && setDeletingUser(null)}
        onConfirm={handleDeleteUser}
        title="Supprimer l'utilisateur"
        description={deletingUser ? `Êtes-vous sûr de vouloir supprimer l'utilisateur "${deletingUser.email}" ? Cette action est irréversible.` : ''}
        itemName={deletingUser?.email}
        isLoading={isDeleting}
      />
    </div>
  )
}
